import React, { useEffect, useState } from "react";
import { Loader2, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { ai, useAT } from "@/lib/aiApi";

const err = (e) => toast.error(e?.response?.data?.detail?.toString?.() || "Error");

const PackEditor = ({ pack, canEdit, onSaved }) => {
  const at = useAT();
  const [f, setF] = useState(null);
  useEffect(() => {
    setF({
      agent_name: pack.agent_name || "", notes: pack.notes || "", sources: (pack.sources || []).join("\n"),
      programs: Object.fromEntries(Object.entries(pack.programs || {}).map(([k, p]) => [k, {
        label: p.label || k, documents: (p.documents || []).join("\n"), services: (p.services || []).join(", ") }])),
    });
  }, [pack]);
  if (!f) return null;
  const save = async () => {
    try {
      const body = {
        agent_name: f.agent_name, notes: f.notes, sources: f.sources.split("\n").map((s) => s.trim()).filter(Boolean),
        programs: Object.fromEntries(Object.entries(f.programs).map(([k, p]) => [k, {
          label: p.label, documents: p.documents.split("\n").map((s) => s.trim()).filter(Boolean),
          services: p.services.split(",").map((s) => s.trim()).filter(Boolean) }])),
      };
      onSaved(await ai.saveKnowledge(pack.destination, body));
      toast.success(at("save"));
    } catch (e) { err(e); }
  };
  const reset = async () => { try { onSaved(await ai.resetKnowledge(pack.destination)); } catch (e) { err(e); } };
  const setP = (k, key, v) => setF((x) => ({ ...x, programs: { ...x.programs, [k]: { ...x.programs[k], [key]: v } } }));
  return (
    <div className="space-y-4" data-testid={`ai-pack-${pack.destination}`}>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-slate-400">{at("agentName")}
          <Input disabled={!canEdit} className="mt-1 bg-slate-950 border-slate-700" value={f.agent_name} onChange={(e) => setF({ ...f, agent_name: e.target.value })} />
        </label>
        <p className="text-xs text-slate-500 self-end">{at("updated")}: {(pack.updated_at || "").slice(0, 10)} · {pack.updated_by}</p>
      </div>
      <label className="block text-xs text-slate-400">{at("notes")}
        <Textarea disabled={!canEdit} rows={12} className="mt-1 bg-slate-950 border-slate-700 font-mono text-xs" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </label>
      <label className="block text-xs text-slate-400">{at("sources")}
        <Textarea disabled={!canEdit} rows={3} className="mt-1 bg-slate-950 border-slate-700 text-xs" value={f.sources} onChange={(e) => setF({ ...f, sources: e.target.value })} />
      </label>
      <h3 className="text-sm font-semibold">{at("programs")}</h3>
      <div className="grid gap-4 lg:grid-cols-2">
        {Object.entries(f.programs).map(([k, p]) => (
          <div key={k} className="rounded-xl border border-slate-800 p-3 space-y-2">
            <p className="font-medium text-sm">{p.label} <span className="text-xs text-slate-500">({k})</span></p>
            <label className="block text-xs text-slate-400">{at("documents")}
              <Textarea disabled={!canEdit} rows={7} className="mt-1 bg-slate-950 border-slate-700 text-xs" value={p.documents} onChange={(e) => setP(k, "documents", e.target.value)} />
            </label>
            <label className="block text-xs text-slate-400">{at("services")}
              <Input disabled={!canEdit} className="mt-1 bg-slate-950 border-slate-700 text-xs" value={p.services} onChange={(e) => setP(k, "services", e.target.value)} />
            </label>
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="flex gap-2">
          <Button className="bg-venka-green text-slate-950 hover:bg-venka-green-light" onClick={save} data-testid="ai-pack-save">{at("save")}</Button>
          <Button variant="outline" className="border-slate-700 bg-slate-900" onClick={reset}><RotateCcw className="h-4 w-4 mr-1" />{at("reset")}</Button>
        </div>
      )}
    </div>
  );
};

export const PortalAi = () => {
  const at = useAT();
  const { isAdmin } = useAuth();
  const [packs, setPacks] = useState(null);
  const [status, setStatus] = useState(null);
  const [sel, setSel] = useState("");
  useEffect(() => {
    ai.knowledge().then((p) => { setPacks(p); setSel((s) => s || p[0]?.destination || ""); }).catch(() => setPacks([]));
    ai.status().then(setStatus).catch(() => {});
  }, []);
  if (!packs) return <Loader2 className="h-5 w-5 animate-spin text-slate-500" />;
  const pack = packs.find((p) => p.destination === sel);
  return (
    <div className="space-y-5" data-testid="portal-ai">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Sparkles className="h-6 w-6 text-fuchsia-300" />{at("knowledge")}</h1>
        <p className="text-sm text-slate-400">{at("knowledgeSub")}</p>
        {status && <p className="text-xs text-slate-500 mt-1">{at("provider")}: {status.provider} · {status.model}</p>}
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-slate-800">
        {packs.map((p) => (
          <button key={p.destination} type="button" onClick={() => setSel(p.destination)}
            className={`whitespace-nowrap px-3 py-2 text-sm border-b-2 -mb-px ${sel === p.destination ? "border-fuchsia-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
            {p.agent_name || p.name}
          </button>
        ))}
      </div>
      {pack && <PackEditor pack={pack} canEdit={isAdmin} onSaved={(np) => setPacks((ps) => ps.map((x) => (x.destination === np.destination ? np : x)))} />}
    </div>
  );
};

export default PortalAi;
